import {
  createEventData,
  getFrontendConfigValue,
  logInIntercomUser,
  logOutIntercomUser,
  pushToDataLayer,
} from '@arnold/common';
import cookies from 'js-cookie';

export const ACCESS_TOKEN_PARAM = 'accessToken';
const DEVICE_TOKEN_COOKIE = 'deviceToken';
const INTERCOM_HASH_COOKIE = 'intercomHash';
const USER_ID_COOKIE = 'userId';
const USER_EMAIL_COOKIE = 'userEmail';
const SSO_SESSION_COOKIE = 'sso_session';
const COOKIE_DOMAIN = getFrontendConfigValue('COOKIE_DOMAIN') || 'arnold-robot.com';

const SHARED_COOKIE_OPTIONS = { path: '/', domain: COOKIE_DOMAIN, expires: 60 };

export type ErrorWithDetails = Error & { details?: any; status?: number };

class Auth {
  getDeviceToken() {
    const cookieToken = cookies.get(DEVICE_TOKEN_COOKIE);
    if (cookieToken) {
      return cookieToken;
    }
    // Generate random token of length 20
    const newToken = Array.from({ length: 20 }, () => Math.floor(Math.random() * 36).toString(36)).join('');
    cookies.set(DEVICE_TOKEN_COOKIE, newToken, SHARED_COOKIE_OPTIONS);
    return newToken;
  }

  setUserId(userId: string) {
    cookies.set(USER_ID_COOKIE, userId, SHARED_COOKIE_OPTIONS);
  }

  getUserId() {
    return cookies.get(USER_ID_COOKIE);
  }

  setIntercomHash(intercomHash?: string) {
    if (intercomHash) {
      cookies.set(INTERCOM_HASH_COOKIE, intercomHash, SHARED_COOKIE_OPTIONS);
    }
  }

  getIntercomHash() {
    return cookies.get(INTERCOM_HASH_COOKIE);
  }

  setUserEmail(email: string) {
    cookies.set(USER_EMAIL_COOKIE, email, SHARED_COOKIE_OPTIONS);
  }

  getUserEmail() {
    return cookies.get(USER_EMAIL_COOKIE);
  }

  async login(username?: string, password?: string, otp?: string, redirectAfterLogin?: string, accessToken?: string) {
    const response = await fetch(getFrontendConfigValue('API_URL') + '/auth/session', {
      body: JSON.stringify({ username, password, deviceToken: this.getDeviceToken(), otp, origin: 'SELF_CARE' }),
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      const r = await response.json();
      const error: ErrorWithDetails = new Error(`fetch error, status ${response.status}`);
      error.status = response.status;
      error.details = r;
      throw error;
    }

    const { id, orgId, intercomHash, createdAt, redirectToTalk } = await response.json();
    this.setUserId(id.toString());
    if (username) {
      this.setUserEmail(username);
    }

    pushToDataLayer({
      userId: id,
      orgId,
      event: 'ux.user-login',
      ...createEventData('user', 'login', 'user login'),
    });

    logInIntercomUser(id, intercomHash, new Date(createdAt));
    this.setIntercomHash(intercomHash);
    if (redirectToTalk && redirectAfterLogin === '/overview') {
      window.location.href = `${getFrontendConfigValue('ARNOLD_URL')}/overview`;
    } else if (redirectAfterLogin) {
      window.location.href = redirectAfterLogin;
    }
  }

  async logout() {
    // We only clear the Arnold session - logging the user out of their SSO
    // identity provider (e.g. their whole Microsoft account) is not desired.
    this.removeUserCookies();
    await fetch(getFrontendConfigValue('API_URL') + '/auth/session', {
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      method: 'DELETE',
      credentials: 'include',
    }).catch((err) => {
      // We don't care about the result. If this fails, server will have stale session but
      // it's servers business only and client doesn't care
    });

    logOutIntercomUser();

    return true;
  }

  setSsoSession(isSso: boolean) {
    if (isSso) {
      cookies.set(SSO_SESSION_COOKIE, 'true', SHARED_COOKIE_OPTIONS);
    } else {
      cookies.remove(SSO_SESSION_COOKIE, SHARED_COOKIE_OPTIONS);
    }
  }

  isSsoSession() {
    return cookies.get(SSO_SESSION_COOKIE) === 'true';
  }

  removeUserCookies() {
    cookies.remove(INTERCOM_HASH_COOKIE, SHARED_COOKIE_OPTIONS);
    cookies.remove(USER_ID_COOKIE, SHARED_COOKIE_OPTIONS);
    cookies.remove(USER_EMAIL_COOKIE, SHARED_COOKIE_OPTIONS);
    this.setSsoSession(false);
  }
}

const auth = new Auth();
export default auth;
